import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { IconProps } from '~/client/src/shared/components/icons/Icon'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { IListSelectorItem } from '~/client/src/shared/components/ListSelector/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

import { IMapViewPublishedTo } from './MapViewsSidebarListItem'
import { mergePublishToDraft } from './mergePublishToDraft'
import { PublishToAppKey, PUBLISH_TO_APP_DESCRIPTORS } from './PublishToChipRow'

// Figma 12507:192723.

interface IAppItem extends IListSelectorItem {
  key: PublishToAppKey
  name: string
  Icon: React.ComponentType<IconProps>
}

const buildItems = (availableApps: PublishToAppKey[]): IAppItem[] =>
  availableApps.map(key => {
    const { label, Icon } = PUBLISH_TO_APP_DESCRIPTORS[key]
    return { id: key, key, name: label(), Icon }
  })

const searchFn = (item: IAppItem, query: string): boolean =>
  item.name.toLowerCase().includes(query.trim().toLowerCase())

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** `null` keeps the popover unmounted. */
  anchor: HTMLElement | null
  availableApps: PublishToAppKey[]
  publishedTo: IMapViewPublishedTo
  onApply: (next: IMapViewPublishedTo) => void
}

// Shape mirrors LocationAssociationPopover's arrow — see that file for the
// floating-ui positioning rationale.
const PopoverArrow: React.FC = () => (
  <PopoverPrimitive.Arrow
    className={cn(
      'tw:flex tw:items-center tw:justify-center',
      'tw:data-[side=right]:-translate-x-full',
      'tw:data-[side=left]:left-auto tw:data-[side=left]:right-0',
      'tw:data-[side=left]:translate-x-full tw:data-[side=left]:rotate-180',
    )}
  >
    <svg width={10} height={20} viewBox="0 0 10 20" aria-hidden="true">
      <path d="M10 0 L0 10 L10 20 Z" className="tw:fill-popover" />
      <path
        d="M10 0 L0 10 L10 20"
        fill="none"
        strokeWidth={1}
        className="tw:stroke-foreground/10"
      />
    </svg>
  </PopoverPrimitive.Arrow>
)

export const PublishToPopover: React.FC<IProps> = ({
  open,
  onOpenChange,
  anchor,
  availableApps,
  publishedTo,
  onApply,
}) => {
  // Snapshot availableApps/publishedTo at session boundaries (open false→true OR anchor retarget)
  // so parent re-renders don't reset ListSelector's draft to persisted values mid-edit.
  // Anchor is the natural session key — fresh open or chip retarget swaps it; renders don't.
  const sessionAnchor = open ? anchor : null
  // Deps intentionally excludes availableApps/publishedTo — capture them on session start only.
  const session = React.useMemo(
    () => ({ availableApps, publishedTo }),
    [sessionAnchor],
  )

  const items = React.useMemo(
    () => buildItems(session.availableApps),
    [session],
  )

  const selectedIds = React.useMemo(
    () =>
      new Set<string>(
        session.availableApps.filter(key => session.publishedTo[key]),
      ),
    [session],
  )

  const handleApply = (draft: Set<string>): void => {
    onApply(
      mergePublishToDraft({
        live: publishedTo,
        snapshot: session.publishedTo,
        availableApps: session.availableApps,
        draft,
      }),
    )
    onOpenChange(false)
  }

  const handleClose = (): void => onOpenChange(false)

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          anchor={anchor}
          side="right"
          align="start"
          sideOffset={12}
          // Keeps the arrow tip clear of the popup's 8px rounded corners.
          arrowPadding={14}
          className={cn('tw:isolate tw:z-50')}
        >
          <PopoverPrimitive.Popup
            data-slot="popover-content"
            // Fixed h=360 fits all four apps inline; CardList virtualizer needs an explicit
            // ancestor height (auto collapses to 0px). max-h caps and scrolls inside if more apps land.
            className={cn(
              'tw:relative tw:flex tw:w-[400px] tw:h-[360px] tw:max-h-(--available-height) tw:origin-(--transform-origin) tw:flex-col tw:rounded-lg tw:bg-popover tw:text-popover-foreground tw:shadow-md tw:ring-1 tw:ring-foreground/10 tw:outline-hidden tw:overflow-hidden tw:duration-100 tw:data-open:animate-in tw:data-open:fade-in-0 tw:data-open:zoom-in-95 tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-closed:zoom-out-95',
              // Inset content away from the edge-frame mask so selected rows don't render under it.
              'tw:[[data-side=right]_&]:pl-2.5 tw:[[data-side=left]_&]:pr-2.5',
            )}
          >
            {/* Edge-frame mask mirrors arrow side; same trick as LocationAssociationPopover. */}
            <span
              aria-hidden="true"
              className="tw:hidden tw:[[data-side=right]_&]:block tw:absolute tw:inset-y-0 tw:left-0 tw:w-2.5 tw:bg-popover tw:pointer-events-none tw:z-10"
            />
            <span
              aria-hidden="true"
              className="tw:hidden tw:[[data-side=left]_&]:block tw:absolute tw:inset-y-0 tw:right-0 tw:w-2.5 tw:bg-popover tw:pointer-events-none tw:z-10"
            />
            <ListSelector.Root<IAppItem>
              items={items}
              selectedIds={selectedIds}
              draft
              onClose={handleClose}
              search={{ searchFn }}
              className="tw:flex-1 tw:min-h-0"
            >
              <ListSelector.Title
                title={Localization.translator.publishTo}
                onClose={handleClose}
              />
              <ListSelector.Toolbar />
              <ListSelector.Counter
                formatTotal={Localization.translator.xApplicationsCount}
              />
              <ListSelector.List<IAppItem>
                renderItemContent={item => (
                  <>
                    <item.Icon size={16} className="tw:shrink-0" />
                    <span className="tw:truncate">{item.name}</span>
                  </>
                )}
              />
              <ListSelector.Actions
                onApply={handleApply}
                onClose={handleClose}
              />
            </ListSelector.Root>
          </PopoverPrimitive.Popup>
          {/* Arrow renders AFTER the Popup so its body paints over the Popup's ring-1 seam.
              Sibling (not child) so the Popup's overflow-hidden doesn't clip it. */}
          <PopoverArrow />
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
